interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  ariaLabel?: string;
}

/**
 * Tiny inline-SVG sparkline. No deps, ~150B per instance, designed to live
 * inside a table cell. Flat line when data is all-zero so the row doesn't
 * look broken. Pads y-axis by 1 to keep a single-tall spike from looking
 * pixel-aligned to the top edge.
 */
export function Sparkline({
  data,
  width = 72,
  height = 20,
  ariaLabel,
}: SparklineProps) {
  if (data.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const max = Math.max(1, ...data);
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data
    .map((v, i) => {
      const x = i * stepX;
      const y = height - (v / max) * (height - 2) - 1;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  // Filled area underneath the line for visual weight.
  const areaPoints = `0,${height} ${points} ${width},${height}`;

  return (
    <svg
      role="img"
      aria-label={ariaLabel ?? 'Activity sparkline'}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
    >
      <polygon
        points={areaPoints}
        className="fill-primary/15"
      />
      <polyline
        points={points}
        fill="none"
        strokeWidth={1.25}
        className="stroke-primary"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
